import type { Virtualizer } from "@tanstack/react-virtual";
import cn from "classnames";
import { type RefObject, useEffect, useState } from "react";
import { capitalize } from "../../lib";
import type { EmojiItem, EmojiLine } from "../../model";
import { EmojiPanelLine } from "../EmojiPanelLine";
import styles from "./EmojiPanelVirtualList.module.scss";

type Props = {
    size: "s" | "m";
    onEmojiClick: (emoji: EmojiItem) => void;
    virtualizer: Virtualizer<HTMLDivElement, Element>;
    rowList: EmojiLine[];
    virtualListRef: RefObject<HTMLDivElement>;
};

export function EmojiPanelVirtualList({
    virtualListRef,
    rowList,
    size,
    onEmojiClick,
    virtualizer,
}: Props) {
    const [containerHeight, setContainerHeight] = useState<number>(0);

    useEffect(() => {
        const updateHeight = () => {
            if (virtualListRef.current) {
                const rect = virtualListRef.current.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const availableHeight = windowHeight - rect.top - 12; // 12px отступ снизу

                // Учитываем размер панели для разных размеров
                const minHeight = size === "s" ? 150 : 200;
                const maxHeight = size === "s" ? windowHeight * 0.6 : windowHeight * 0.8;

                const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, availableHeight));
                setContainerHeight(calculatedHeight);
            }
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        if (virtualListRef.current) {
            resizeObserver.observe(virtualListRef.current);
        }

        window.addEventListener("resize", updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateHeight);
        };
    }, [virtualListRef, size]);

    return (
        <div
            ref={virtualListRef}
            className={cn(
                styles.emojiPanelListWrapper,
                styles[`emojiPanelListWrapperSize${capitalize(size)}`],
                "scrollable",
            )}
            style={{ height: containerHeight > 0 ? `${containerHeight}px` : undefined }}
        >
            <div
                style={{ height: `${virtualizer.getTotalSize()}px` }}
                data-testid="EmojiPanel.InnerList"
            >
                <div
                    className={cn(
                        styles.emojiPanelList,
                        styles[`emojiPanelListSize${capitalize(size)}`],
                    )}
                    style={{
                        transform: `translateY(${virtualizer.getVirtualItems()[0]?.start}px)`,
                    }}
                >
                    {virtualizer.getVirtualItems().map((virtualRow) => {
                        const item = rowList[virtualRow.index];

                        return (
                            <div key={virtualRow.index} style={{ height: `${item.height}px` }}>
                                {item.type === "title" && (
                                    <h4
                                        data-testid="EmojiPanel.DataGroup"
                                        data-group={item.group}
                                        data-grouppy
                                        className={cn(
                                            styles.emojiGroupTitle,
                                            styles[`emojiGroupTitleSize${capitalize(size)}`],
                                        )}
                                    >
                                        {item.value}
                                    </h4>
                                )}

                                {item.type === "emojiList" && (
                                    <EmojiPanelLine
                                        key={item.key}
                                        emojiGroup={item.group}
                                        emojiList={item.value}
                                        size={size}
                                        disableTooltip={virtualizer.isScrolling}
                                        onClick={onEmojiClick}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
